import { useCallback, useMemo, useState } from 'react';

import { AxiosResponse } from 'axios';

import { DETAILS_DATA_TYPES } from 'store/details/utils/constants.ts';
import isVodEditorialsResponse from 'store/details/utils/isVodEditorialsResponse.ts';
import {
    BTVProgrammesResponse,
    ResponseError,
    VodResponse,
} from 'store/railsBuilder/reducer.types.ts';

import { VodEditorial } from 'services/data-manager/types.ts';

import { noSeasonName } from '../constants.ts';
import { EpisodesState } from '../types.ts';
import requestNoSeasonEpisodesData from '../utils/requestNoSeasonEpisodesData.ts';
import requestSeasonEpisodesData from '../utils/requestSeasonEpisodesData.ts';

type Params = {
    seriesId: string;
    channelId: string | undefined;
    initialSeason: number | typeof noSeasonName;
    selectedSeasonNumber: number | typeof noSeasonName;
};

const EPISODES_LIMIT = 10;

function useVodEpisodes({
    seriesId,
    channelId,
    initialSeason,
    selectedSeasonNumber,
}: Params) {
    const [vodsState, setVodsState] = useState<EpisodesState<VodEditorial>>({
        [initialSeason]: { episodes: [], page: 1, maxPages: undefined },
    });

    const episodes = useMemo(
        () => vodsState[selectedSeasonNumber]?.episodes || [],
        [vodsState, selectedSeasonNumber],
    );

    const handleLoadVODSeasonEpisodes = useCallback(
        async (
            _seriesId: string,
            _channelId: string | undefined,
            _seasonNumber: typeof initialSeason,
            _page: number,
        ) => {
            let vodEpisodesBySeason:
                | AxiosResponse<
                      BTVProgrammesResponse | VodResponse | ResponseError
                  >
                | undefined;

            if (_seasonNumber === noSeasonName) {
                vodEpisodesBySeason = await requestNoSeasonEpisodesData(
                    DETAILS_DATA_TYPES.VOD,
                    _seriesId,
                    _channelId,
                    _page,
                    EPISODES_LIMIT,
                );
            } else {
                vodEpisodesBySeason = await requestSeasonEpisodesData(
                    DETAILS_DATA_TYPES.VOD,
                    _seasonNumber,
                    _seriesId,
                    _channelId,
                    _page,
                    EPISODES_LIMIT,
                );
            }

            if (isVodEditorialsResponse(vodEpisodesBySeason)) {
                setVodsState((prevState) => ({
                    ...prevState,
                    [_seasonNumber]: {
                        ...prevState[_seasonNumber],
                        episodes:
                            _page === 1
                                ? vodEpisodesBySeason.data.editorials
                                : [
                                      ...(prevState[_seasonNumber]?.episodes ||
                                          []),
                                      ...vodEpisodesBySeason.data.editorials,
                                  ],
                        page: _page,
                        maxPages:
                            _page === 1
                                ? Math.ceil(
                                      vodEpisodesBySeason.data.total_records /
                                          EPISODES_LIMIT,
                                  )
                                : prevState[_seasonNumber]?.maxPages,
                    },
                }));
            }
        },
        [],
    );

    const handleLoadNextEpisodes = useCallback(async () => {
        const { page, maxPages } = vodsState[selectedSeasonNumber] || {};
        const nextPage = page + 1;

        if (maxPages != null && nextPage <= maxPages) {
            await handleLoadVODSeasonEpisodes(
                seriesId,
                channelId,
                selectedSeasonNumber,
                nextPage,
            );
        }
    }, [
        channelId,
        handleLoadVODSeasonEpisodes,
        selectedSeasonNumber,
        seriesId,
        vodsState,
    ]);

    return {
        episodes,
        onLoadEpisodes: handleLoadVODSeasonEpisodes,
        onLoadNextEpisodes: handleLoadNextEpisodes,
    };
}

export default useVodEpisodes;
